import type { NameColumnConfig } from './types';

// Name-column detection + extraction. Ported from CPAiSuite Universal
// (spec §4.4 / Tech Spec §6.4).

/**
 * Detect the employee-name column layout from a header row.
 *
 * Priority:
 *  1. Two adjacent columns matching /last.?name/i and /first.?name/i -> combined.
 *  2. A single column that is exactly employee|worker|associate|staff,
 *     or contains "employee name" | "emp name" | "full name" -> single.
 *  3. The first column whose header contains "name" (substring) -> single.
 *  4. Nothing found -> null (the user picks manually).
 */
export function findNameColumns(headerRow: string[]): NameColumnConfig {
	const headers = headerRow.map((h) => String(h ?? ''));

	// 1. Adjacent Last Name / First Name columns (either order).
	for (let i = 0; i < headers.length - 1; i++) {
		const a = headers[i];
		const b = headers[i + 1];
		if (/last.?name/i.test(a) && /first.?name/i.test(b)) {
			return {
				type: 'combined',
				lastNameIndex: i,
				firstNameIndex: i + 1,
				lastHeader: a,
				firstHeader: b,
			};
		}
		if (/first.?name/i.test(a) && /last.?name/i.test(b)) {
			return {
				type: 'combined',
				lastNameIndex: i + 1,
				firstNameIndex: i,
				lastHeader: b,
				firstHeader: a,
			};
		}
	}

	// 2. Strong single-column match.
	const strongIdx = headers.findIndex(
		(h) =>
			/^(employee|worker|associate|staff)$/i.test(h.trim()) ||
			/employee name|emp name|full name/i.test(h)
	);
	if (strongIdx !== -1) {
		return { type: 'single', index: strongIdx, header: headers[strongIdx] };
	}

	// 3. Substring "name" fallback.
	const nameIdx = headers.findIndex((h) => /name/i.test(h));
	if (nameIdx !== -1) {
		return { type: 'single', index: nameIdx, header: headers[nameIdx] };
	}

	// 4. No name column detected.
	return null;
}

/**
 * Extract the employee name from a data row per the detected config.
 * - single: the cell value as-is (normalization is normalizeEmployeeName's job).
 * - combined: "Last, First"; if one side is empty, use only the non-empty side;
 *   if both are empty, return '' (blank).
 * - multi (ENG-409): trim each mapped column's value, drop empties, and join the
 *   rest with a single space (columns already in file order). '' (blank) if all
 *   are empty. Works for 1..N columns.
 *   ENG-421: when `lastNameIndex` is set, the surname column is known, so assemble
 *   "Last, First Middle" directly (mirrors the `combined` branch) instead of the
 *   space-join. Without it, a 3+-column layout like ["Thornton","Billy","Bob"]
 *   space-joins to "Thornton Billy Bob", which normalizeEmployeeName then misreads
 *   as First…Last and reorders to "Bob, Thornton Billy" — the wrong person, and
 *   still comma-less until normalization guesses. With lastNameIndex it produces
 *   "Thornton, Billy Bob" up front, so no guessing is needed.
 *
 * ENG-369: an all-empty name returns '' (never the string "Unknown"). A blank
 * name is a data problem the user must resolve in Step 3 — computeEmployeeRows
 * flags '' rows as 'blank_name' so they surface as unresolved errors rather than
 * silently becoming a fake "Unknown" employee. The single branch already returned
 * '' for a blank cell; combined/multi now match it.
 */
export function getEmployeeName(
	row: string[],
	nameCol: NonNullable<NameColumnConfig>
): string {
	if (nameCol.type === 'single') {
		return String(row[nameCol.index] ?? '').trim();
	}

	if (nameCol.type === 'multi') {
		const trim = (i: number) => String(row[i] ?? '').trim();

		// ENG-421: surname column identified — assemble "Last, First Middle".
		if (nameCol.lastNameIndex !== undefined) {
			const last = trim(nameCol.lastNameIndex);
			const rest = nameCol.indices
				.filter((i) => i !== nameCol.lastNameIndex)
				.map(trim)
				.filter((v) => v !== '');
			if (last && rest.length > 0) return `${last}, ${rest.join(' ')}`;
			// Missing side: fall back to whichever part is present (as `combined` does).
			if (last) return last;
			return rest.length > 0 ? rest.join(' ') : '';
		}

		const parts = nameCol.indices.map(trim).filter((v) => v !== '');
		return parts.length > 0 ? parts.join(' ') : '';
	}

	const last = String(row[nameCol.lastNameIndex] ?? '').trim();
	const first = String(row[nameCol.firstNameIndex] ?? '').trim();
	if (last && first) return `${last}, ${first}`;
	if (last) return last;
	if (first) return first;
	return '';
}
